import React, { useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import ItemGeneralDataComponent from './ItemGeneralDataComponent';
import { ITEM_GENERAL_DATA_QUERY } from '../queries';
import { ITEM_TYPES_QUERY } from '../../list/queries';
import { SAVE_ITEM_GENERAL_DATA } from '../mutations';
import { ITEM_GENERAL_DATA_SAVED } from '../subscriptions';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { itemValidator } from '../../validators';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { itemNameVar } from '../variables';
import { useToast } from 'hooks/toasts/useToast';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';

const ItemGeneralDataContainer = ({ itemId }) => {
  const historyContext = useHistoryWithContext();
  const addToast = useToast();
  const { t } = useTranslation();
  const client = useApolloClient();

  // #region state
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [validation, validate] = useDirtyFieldValidation(itemValidator);
  const [saving, setSaving] = useState(false);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  // #endregion

  // #region queries
  const { loading } = useQuery(ITEM_GENERAL_DATA_QUERY, {
    variables: {
      id: itemId
    },
    skip: !itemId,
    onCompleted: data => {
      resetLens(data.itemGeneralData);
    }
  });

  const { data: itemTypesData } = useQuery(ITEM_TYPES_QUERY);
  // #endregion

  // #region mutations
  const [saveItemGeneralData] = useMutation(SAVE_ITEM_GENERAL_DATA, {
    onError: () => {
      setSaving(false);
    }
  });
  // #endregion

  // #region subscriptions
  useSubscription(ITEM_GENERAL_DATA_SAVED, {
    onData: ({
      data: {
        data: { itemGeneralDataSaved }
      }
    }) => {
      if (!itemId) {
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'items' });
        client.cache.gc();
        historyContext.push(`/items/${itemGeneralDataSaved.item.id}`);
      }

      setSaving(false);
      addToast(t('Items.ItemSaved'), 'success');
    }
  });
  // #endregion

  // #region handlers
  const handleSave = useCallback(() => {
    const generalData = get(lens);
    if (!validate(generalData)) return;
    setSaving(true);
    saveItemGeneralData({ variables: { input: { ...generalData } } });
  }, [lens, saveItemGeneralData, validate]);
  // #endregion

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  useEffect(() => {
    const code = get(lens.code) ? get(lens.code) : '-';
    const name = get(lens.name) ? get(lens.name) : '-';
    const concatenatedNameVar = `${code} / ${name}`;
    itemNameVar(concatenatedNameVar);
  }, [lens.code, lens.name]);

  return loading ? (
    <Card>
      <FakeText lines={5} />
    </Card>
  ) : (
    <ItemGeneralDataComponent
      lens={lens}
      itemTypes={itemTypesData?.itemTypes}
      onSave={handleSave}
      saving={saving}
      validation={validation}
      isDirty={isDirty}
    />
  );
};

ItemGeneralDataContainer.propTypes = {
  itemId: PropTypes.number
};

export default ItemGeneralDataContainer;
